import React from 'react';

import {
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';

import ContentCard from '../../../../../components/ContentCard';
import Link from '../../../../../components/Link';
import { useGetClassQuery } from '../../../../../services/classes';
import { useGetCourseQuery } from '../../../../../services/courses';
import { useGetStreamQuery } from '../../../../../services/streams';
import { Class } from '../../../../../services/students/types';
import {
  getClassId, getClassSubject,
} from '../../../../../utils/common/classes';
import {
  Header, HeadingRow, SelectableRow,
} from '../../../index.styles';

interface ClassRowProps {
  classCompositeKey: string,
}

const ClassRow = ({ classCompositeKey }: ClassRowProps) => {
  const subject = getClassSubject(classCompositeKey);
  const classId = getClassId(classCompositeKey);

  const { data: classObj, isLoading: isClassLoading } = useGetClassQuery({ subject, classId });
  const { data: course, isLoading: isCourseLoading } = useGetCourseQuery({
    courseId: classObj?.data.course || '',
  }, { skip: !classObj?.data });
  const { data: stream, isLoading: isStreamLoading } = useGetStreamQuery({
    course: classObj?.data.course || '',
    streamId: classObj?.data.stream || '',
  }, { skip: !classObj?.data });

  if (isClassLoading || isCourseLoading || isStreamLoading) {
    return (
      <Skeleton variant="rectangular" height={32} />
    );
  }

  return (
    <SelectableRow>
      <Link href={`/admin/class/${subject}/${classId}`} color="inherit" underline="none">
        <Grid container direction="row" spacing={1} alignItems="center">
          <Grid item xs={3}>
            <Stack direction="column">
              <Typography variant="body1">
                {course?.data.shortLabel}
              </Typography>
              <Typography variant="caption">
                {`(${stream?.data.shortLabel})`}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body1">
              {classObj?.data.teacherName}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body1">
              {(classObj?.data?.day === undefined
            || classObj?.data?.time === undefined
            || classObj?.data.day === -1)
                ? 'N/A'
                : dayjs().day(classObj.data.day).format('ddd')} {dayjs(classObj?.data.time, 'HHmm').format('h:mm a')}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body1">
              {classId}
            </Typography>
          </Grid>
        </Grid>
      </Link>
    </SelectableRow>
  );
};

interface ClassesCardProps {
  classes: Class[]
}

const ClassesCard = ({ classes }: ClassesCardProps) => (
  <ContentCard title="Classes">
    {classes && classes.length > 0 ? (
      <Stack direction="column">
        <HeadingRow>
          <Grid container direction="row" spacing={1}>
            <Grid item xs={3}>
              <Header variant="subtitle2">Course / Stream</Header>
            </Grid>
            <Grid item xs={3}>
              <Header variant="subtitle2">Teacher</Header>
            </Grid>
            <Grid item xs={3}>
              <Header variant="subtitle2">Day / Time</Header>
            </Grid>
            <Grid item xs={3}>
              <Header variant="subtitle2">Name</Header>
            </Grid>
          </Grid>
        </HeadingRow>
        <>
          {(classes).map((c) => (
            <ClassRow key={c.classId} classCompositeKey={c.classId} />
          ))}
        </>
      </Stack>
    ) : (
      <Typography variant="overline">No classes</Typography>
    )}
  </ContentCard>
);

export default ClassesCard;
